import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { BASE_URL } from '../../apiConfig';
import { RemitoDetalle } from './RemitoDetalle';

function ProductMovements() {
    const [searchTerm, setSearchTerm] = useState('');
    const [productos, setProductos] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [destinos, setDestinos] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);

    const [movementData, setMovementData] = useState({
        type: 'salida',
        quantity: 1,
        destino: '',
        receptor: '',
        observacion: ''
    });

    const contentRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef: contentRef,
    });

    const esSalida = movementData.type === 'salida';
    const bgHeader = esSalida ? 'bg-red-50' : 'bg-green-50';
    const textHeader = esSalida ? 'text-red-600' : 'text-green-600';
    const btnStyle = esSalida ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700';

    useEffect(() => {
        const fetchMasters = async () => {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            try {
                const [dest, recep] = await Promise.all([
                    axios.get(`${BASE_URL}/destinos`, config),
                    axios.get(`${BASE_URL}/receptores`, config)
                ]);
                setDestinos(dest.data);
                setPersonas(recep.data);
            } catch (error) { console.error(error); }
        };
        fetchMasters();
    }, []);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setSelectedProduct(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (value.trim().length >= 2) {
            debounceRef.current = setTimeout(async () => {
                setLoading(true);
                try {
                    const token = localStorage.getItem('token');
                    const res = await axios.get(`${BASE_URL}/productos?search=${value}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setProductos(res.data);
                    setShowDropdown(res.data.length > 0);
                } catch {
                    setIsError(true);
                } finally {
                    setLoading(false);
                }
            }, 300);
        } else {
            setProductos([]);
            setShowDropdown(false);
        }
    };

    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
        setSearchTerm(product.descripcion);
        setShowDropdown(false);
    };

    const limpiarFormulario = () => {
        setSelectedProduct(null);
        setSearchTerm('');
        setProductos([]);
        setShowDropdown(false);
        setMovementData({ type: 'salida', quantity: 1, destino: '', receptor: '', observacion: '' });
        setMessage('Formulario reiniciado');
        setIsError(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedProduct) return;

        if (esSalida && movementData.quantity > selectedProduct.stock) {
            setMessage(`Stock insuficiente (Disponible: ${selectedProduct.stock})`);
            setIsError(true);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${BASE_URL}/productos/movimientos/registrar`, {
                productoId: selectedProduct.id,
                cantidad: parseInt(movementData.quantity),
                tipo: movementData.type,
                razon: `${movementData.destino} - ${movementData.receptor} | ${movementData.observacion}`.trim()
            }, { headers: { Authorization: `Bearer ${token}` } });

            setMessage(`✅ ${movementData.type.toUpperCase()} registrada`);
            setIsError(false);

            setTimeout(() => {
                if (window.confirm("¿Desea imprimir el remito?")) {
                    handlePrint();
                }
                limpiarFormulario();
            }, 500);

        } catch (err) {
            setMessage("Error al registrar");
            setIsError(true);
        }
    };

    return (
        <div className="form-container-responsive animate-fadeIn">
            {/* Header */}
            <div className={`mb-8 p-6 rounded-[2rem] ${bgHeader} transition-all duration-500`}>
                <h2 className={`text-3xl font-bold flex items-center gap-3 ${textHeader}`}>
                    <i className={`bi ${esSalida ? 'bi-box-arrow-right' : 'bi-box-arrow-in-left'}`}></i>
                    Registro de {esSalida ? 'Salida' : 'Entrada'}
                </h2>
            </div>

            {message && (
                <div className={`${isError ? 'bg-red-50 text-red-600' : 'bg-gray-900 text-amber-400'} p-4 rounded-2xl mb-6 text-sm font-bold flex justify-between items-center`}>
                    <span>{message}</span>
                    <button onClick={() => setMessage('')}>✕</button>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                    <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm">
                        <label className="label-modern">Buscar Item</label>

                        {/* Autocomplete */}
                        <div className="relative" ref={dropdownRef}>
                            <div className="relative flex items-center">
                                <input
                                    type="text"
                                    className="input-modern pr-10"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onFocus={() => productos.length > 0 && setShowDropdown(true)}
                                    placeholder="Escriba para buscar..."
                                    autoComplete="off"
                                />
                                <span className="absolute right-3 text-gray-400 pointer-events-none">
                                    {loading
                                        ? <i className="bi bi-arrow-repeat animate-spin"></i>
                                        : <i className="bi bi-search text-sm"></i>
                                    }
                                </span>
                            </div>

                            {showDropdown && (
                                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl z-20 mt-1 max-h-64 overflow-y-auto">
                                    {productos.map(p => (
                                        <div
                                            key={p.id}
                                            onMouseDown={() => handleSelectProduct(p)}
                                            className="p-3 cursor-pointer hover:bg-blue-50 border-b border-gray-50 last:border-0"
                                        >
                                            <p className="text-sm font-bold text-gray-800">{p.descripcion}</p>
                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                Stock disponible: <span className="font-bold">{p.stock}</span>
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Info del producto seleccionado */}
                        {selectedProduct && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-2xl border border-blue-100">
                                <p className="text-xs font-black text-blue-400 uppercase tracking-wider mb-1">Seleccionado</p>
                                <p className="text-sm font-bold text-blue-800">{selectedProduct.descripcion}</p>
                                <p className="text-xs text-blue-500 mt-1">Stock: <span className="font-bold">{selectedProduct.stock}</span></p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-8">
                    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                        {/* Selector de Producto Seleccionado */}
                        <div className={`mb-6 p-4 rounded-2xl border-2 border-dashed ${esSalida ? 'border-red-100' : 'border-green-100'}`}>
                            <span className="text-[10px] uppercase font-black text-gray-400 block">Item:</span>
                            <span className="text-lg font-bold">{selectedProduct?.descripcion || "---"}</span>
                        </div>

                        {/* Campos de Entrada/Salida y Cantidad */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <select name="type" className={`input-modern font-bold ${textHeader}`} value={movementData.type} onChange={(e) => setMovementData({...movementData, type: e.target.value})}>
                                <option value="salida">SALIDA (-)</option>
                                <option value="entrada">ENTRADA (+)</option>
                            </select>
                            <input type="number" name="quantity" className="input-modern" value={movementData.quantity} onChange={(e) => setMovementData({...movementData, quantity: e.target.value})} />
                        </div>

                        {/* Destinos y Personas */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <select name="destino" className="input-modern" value={movementData.destino} onChange={(e) => setMovementData({...movementData, destino: e.target.value})} required>
                                <option value="">Destino...</option>
                                {destinos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
                            </select>
                            <select name="receptor" className="input-modern" value={movementData.receptor} onChange={(e) => setMovementData({...movementData, receptor: e.target.value})} required>
                                <option value="">Responsable...</option>
                                {personas.map(p => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                            </select>
                        </div>

                        {/* Observaciones */}
                        <textarea className="input-modern mb-8 min-h-[100px]" placeholder="Observaciones..." value={movementData.observacion} onChange={(e) => setMovementData({...movementData, observacion: e.target.value})} />

                        {/* BOTONES DE ACCIÓN */}
                        <div className="flex gap-4">
                            <button type="button" onClick={limpiarFormulario} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black hover:bg-gray-200 transition-all">
                                CANCELAR
                            </button>
                            <button type="submit" disabled={!selectedProduct} className={`flex-[2] py-4 text-white rounded-2xl font-black shadow-lg transition-all ${btnStyle} disabled:opacity-50`}>
                                CONFIRMAR {movementData.type.toUpperCase()}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Remito Oculto */}
            <div style={{ display: 'none' }}>
                <RemitoDetalle ref={contentRef} movimiento={movementData} producto={selectedProduct} />
            </div>
        </div>
    );
}

export default ProductMovements;
